import React from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { line, radius, font, surface } from 'features/dashboard/tokens'

const dueChip = deadline => {
  if (!deadline) return { text: 'No deadline', tone: 'muted' }
  const days = moment(deadline).startOf('day').diff(moment().startOf('day'), 'days')
  if (days < 0) return { text: `${Math.abs(days)}d late`, tone: 'bad' }
  if (days === 0) return { text: 'Today', tone: 'warn' }
  if (days === 1) return { text: 'Tomorrow', tone: 'warn' }
  return { text: `${days}d`, tone: days <= 3 ? 'warn' : 'ok' }
}

const TaskRowList = ({ tasks = [], variant = 'upcoming', onOpen }) => {
  const rows = tasks || []
  if (!rows.length) {
    return <Empty>{variant === 'overdue' ? 'Nothing overdue in this view. Nice.' : 'Nothing due in the next 7 days.'}</Empty>
  }

  return (
    <List>
      {rows.map(t => {
        const chip = variant === 'overdue'
          ? { text: `${t.daysOverdue ?? 0}d late`, tone: 'bad' }
          : dueChip(t.deadline)
        return (
          <Row
            key={t.id}
            $clickable={!!onOpen}
            onClick={onOpen ? () => onOpen(t.id) : undefined}
            title={onOpen ? `Open ${t.title}` : undefined}
          >
            {t.domainColour ? <Dot style={{ background: t.domainColour }} /> : <Dot style={{ background: '#cbd5e1' }} />}
            <Main>
              <Title>{t.title || 'Untitled task'}</Title>
              <Sub>{[t.bpName, t.domainName].filter(Boolean).join(' · ') || 'No building set'}</Sub>
            </Main>
            <Chip $tone={chip.tone}>{chip.text}</Chip>
          </Row>
        )
      })}
    </List>
  )
}

export default TaskRowList

const List = styled.div`
  display: flex; flex-direction: column;
`

const Row = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 9px 4px;
  border-bottom: 1px solid ${surface.wash};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  &:hover { background: ${({ $clickable }) => ($clickable ? surface.wash : 'transparent')}; }
  &:last-child { border-bottom: none; }
`

const Dot = styled.span`
  width: 8px; height: 8px; border-radius: 50%; flex: 0 0 auto;
`

const Main = styled.div`
  flex: 1; min-width: 0;
  display: flex; flex-direction: column; gap: 2px;
`

const Title = styled.span`
  font-size: ${font.body};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`

const Sub = styled.span`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`

const Chip = styled.span`
  flex: 0 0 auto;
  font-size: ${font.caption};
  font-weight: 800;
  padding: 3px 9px;
  border-radius: ${radius.pill};
  ${({ $tone }) => $tone === 'bad'
    ? 'background: #b0002014; color: #b00020;'
    : $tone === 'warn'
    ? 'background: #d9770614; color: #d97706;'
    : $tone === 'ok'
    ? 'background: #15803d14; color: #15803d;'
    : 'background: #f1f5f9; color: #64748b;'}
`

const Empty = styled.div`
  padding: 24px; text-align: center;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: ${font.body};
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px dashed ${line.base};
  border-radius: ${radius.md};
`
